




































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import CommonVue from '../models/commonVue';
import Submodule from '../api/submodule';
import LockManager from '../models/lock';
import Media from '../api/media';

import { MediaFileField } from '../models/fields';
import { BusEvent } from '../models/bus';
import FieldInternational from '../components/FieldInternational.vue';
import FieldImage from '../components/FieldImage.vue';
import EditIcon from '../components/icons/EditIcon.vue';
import Module from '@/api/module';

@Component({
  components: {
    'field-international': FieldInternational,
    'field-image': FieldImage,
    'edit-icon': EditIcon
  }
})
export default class EditSubmodule extends CommonVue {
  @Prop({ type: Number }) public moduleId!: number;
  @Prop() public id?: number;
  @Prop({ type: String, default: Module.SLUG }) public parent: string;

  public submodule: Submodule = null;
  public submoduleName: string = '';

  public async beforeMount() {
    await this.load();
  }

  @Watch('id')
  public async load() {
    this.busy = true;
    this.error = false;

    this.ignoreFirstEntityChange = true;
    await this.loadSubmodule();
    this.ignoreFirstEntityChange = false;
    this.updateName();

    if (this.submodule.id) {
      this.lockManager = new LockManager(
        this.SUBMODULE_SLUG,
        this.submodule.id
      );
      await this.lockManager.checkStatus();
      if (this.lockManager.isBusy) {
        this.$bus.$emit(
          BusEvent.ERROR,
          'Someone else is making changes.',
          new Error(
            'Click refresh to check if they have finished their changes'
          )
        );
      }
    } else {
      this.lockManager = null;
    }

    this.busy = false;
  }

  public async loadSubmodule(): Promise<void> {
    if (!this.id) {
      this.submodule = new Submodule();
      this.submodule.parent_module = this.moduleId;
    } else {
      try {
        this.submodule = await Submodule.get(this.id);
      } catch (e) {
        this.error = true;
        this.$bus.$emit(
          BusEvent.ERROR,
          'Could not get submodule information',
          e
        );
      }
    }
  }

  public updateName() {
    let name = this.submodule.name.getDefaultValue();
    if (!name) {
      name = '';
    }
    this.submoduleName = name;
  }

  // enter edit mode if it can get the lock
  @Watch('submodule', { deep: true })
  public async enterEditMode() {
    await this.tryToLock();
  }

  public async deleteClicked(): Promise<void> {
    if (!this.submodule || !this.submodule.id) {
      return;
    }

    this.busy = true;

    try {
      if (!this.lockManager) {
        this.lockManager = new LockManager(
          this.SUBMODULE_SLUG,
          this.submodule.id
        );
      }

      await this.tryToLock();
      await this.submodule.delete();
      await this.lockManager.releaseLock();

      this.busy = false;
      this.$router.push(`/${this.parent}/${this.moduleId}/${this.LESSON_SLUG}`);
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not delete submodule', e);
    }
  }

  public async saveClicked(): Promise<void> {
    if (!this.submodule) {
      return;
    }

    this.busy = true;

    try {
      this.checkMandatoryFields();

      await this.saveSubmodule();

      this.busy = false;
      this.$router.push(`/${this.parent}/${this.moduleId}/${this.LESSON_SLUG}`);
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not save submodule', e);
    }
  }

  public checkMandatoryFields() {
    // Check if name is filled
    const defaultName = this.submodule.name.getDefaultValue();
    if (defaultName === undefined || defaultName === '') {
      throw Error('Please insert an English name for your submodule.');
    }
  }

  public async saveSubmodule(): Promise<void> {
    let moduleLockManager: LockManager = null;

    if (!this.submodule.id) {
      moduleLockManager = new LockManager(this.MODULE_SLUG, this.moduleId);
      await moduleLockManager.tryAcquireLock();
    } else {
      if (!this.lockManager && this.submodule && this.submodule.id) {
        this.lockManager = new LockManager(
          this.SUBMODULE_SLUG,
          this.submodule.id
        );
      }
      await this.tryToLock();
      await this.saveMediaFiles();
    }

    // Upload submodule
    await this.submodule.save();

    if (this.lockManager) {
      await this.lockManager.releaseLock();
    }
    if (moduleLockManager) {
      await moduleLockManager.releaseLock();
    }
  }

  public async saveMediaFiles(): Promise<void> {
    if (!this.submodule || !this.submodule.id) {
      throw Error('Submodule has not been created yet.');
    }

    this.imagesToDelete.forEach(async (id) => await Media.deleteImage(id));
    this.imagesToDelete = [];

    if (!this.isNew) {
      const icon = await this.saveMediaFile(this.$refs.icon, 'icon');
      if (icon !== null) {
        this.submodule.icon = icon;
      }
    }
  }

  public async saveMediaFile(
    field: any,
    fieldName: string
  ): Promise<number | null | undefined> {
    if (!this.submodule || !this.submodule.id) {
      return null;
    }
    const mediaFileField = (field as any) as MediaFileField;
    if (mediaFileField.hasNewMedia()) {
      return await mediaFileField.upload(
        this.SUBMODULE_SLUG,
        this.submodule.id,
        fieldName
      );
    } else if (!mediaFileField.hasMedia()) {
      return undefined;
    } else {
      return null;
    }
  }
}
